/**
 * Shipping Calculator
 * Handles shipping rate calculation for cart page
 */

class ShippingCalculator {
  constructor(container) {
    this.container = container;
    this.calculator = container.querySelector('#shipping-calculator');
    this.responseContainer = container.querySelector('.cart-shipping-calculator-response');
    this.strings = this.getStrings();
    this.moneyFormat = this.strings.moneyFormat || '{{amount}}';
    
    if (!this.calculator) return;
    
    this.init();
  }

  getStrings() {
    const stringsEl = document.querySelector('[data-shipping-calculator-strings]');
    if (!stringsEl) return {};
    try {
      return JSON.parse(stringsEl.textContent);
    } catch (e) {
      return {};
    }
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
      return;
    }

    const countrySelect = this.calculator.querySelector('#address_country');
    const provinceSelect = this.calculator.querySelector('#address_province');
    
    if (!countrySelect || !provinceSelect) return;

    this.waitForShopify(() => {
      this.initializeCountryProvinceSelector();
    });
  }

  waitForShopify(callback, retries = 50) {
    if (typeof Shopify !== 'undefined' && Shopify.CountryProvinceSelector) {
      callback();
      return;
    }
    
    if (retries > 0) {
      setTimeout(() => this.waitForShopify(callback, retries - 1), 100);
    } else {
      this.initializeManualProvinceSelector();
    }
  }

  initializeCountryProvinceSelector() {
    const countrySelect = this.calculator.querySelector('#address_country');
    const provinceSelect = this.calculator.querySelector('#address_province');

    try {
      this.countryProvinceSelector = new Shopify.CountryProvinceSelector(
        'address_country',
        'address_province',
        { hideElement: null }
      );
    } catch (e) {
      this.initializeManualProvinceSelector();
    }

    this.setupEventListeners();
    this.setDefaultValues();
    this.updateSelectLabels();
  }

  initializeManualProvinceSelector() {
    const countrySelect = this.calculator.querySelector('#address_country');
    const provinceSelect = this.calculator.querySelector('#address_province');

    if (!countrySelect || !provinceSelect) return;

    this.waitForCountries(() => {
      this.setupManualProvinceUpdater();
    });
  }

  waitForCountries(callback, retries = 30) {
    if (typeof Countries !== 'undefined' && Countries) {
      callback();
      return;
    }
    
    if (retries > 0) {
      setTimeout(() => this.waitForCountries(callback, retries - 1), 100);
    } else {
      callback();
    }
  }

  setupManualProvinceUpdater() {
    const countrySelect = this.calculator.querySelector('#address_country');
    const provinceSelect = this.calculator.querySelector('#address_province');

    let isUpdating = false;

    const updateProvinces = () => {
      if (isUpdating) return;
      isUpdating = true;

      const selectedCountry = countrySelect.value;
      
      if (!selectedCountry || selectedCountry === '---') {
        provinceSelect.innerHTML = '<option value="">State</option>';
        this.updateSelectLabel(provinceSelect);
        isUpdating = false;
        return;
      }
      
      provinceSelect.innerHTML = '<option value="">State</option>';
      
      if (typeof Countries !== 'undefined' && Countries && Countries[selectedCountry]) {
        const countryData = Countries[selectedCountry];
        if (countryData.provinces && Array.isArray(countryData.provinces) && countryData.provinces.length > 0) {
          countryData.provinces.forEach(province => {
            const option = document.createElement('option');
            if (Array.isArray(province)) {
              option.value = province[1] || province[0];
              option.textContent = province[0];
            } else {
              option.value = province;
              option.textContent = province;
            }
            provinceSelect.appendChild(option);
          });
          
          const defaultProvince = provinceSelect.dataset.default;
          if (defaultProvince && defaultProvince !== '') {
            const matchingOption = Array.from(provinceSelect.options).find(opt => 
              opt.value === defaultProvince || opt.textContent === defaultProvince
            );
            if (matchingOption) {
              provinceSelect.value = matchingOption.value;
            } else if (provinceSelect.options.length > 1) {
              provinceSelect.selectedIndex = 1;
            }
          } else if (provinceSelect.options.length > 1) {
            provinceSelect.selectedIndex = 1;
          }
          
          this.updateSelectLabel(provinceSelect);
          
          provinceSelect.style.display = 'none';
          provinceSelect.offsetHeight;
          provinceSelect.style.display = '';
        }
      }
      
      this.updateSelectLabel(provinceSelect);
      isUpdating = false;
    };

    countrySelect.addEventListener('change', updateProvinces);
    this.setupEventListeners();
    this.updateSelectLabels();
    this.setDefaultValues();
    
    if (countrySelect.value && countrySelect.value !== '---') {
      setTimeout(() => {
        updateProvinces();
      }, 300);
    }
  }

  setupEventListeners() {
    const calculateBtn = this.calculator.querySelector('.get-rates');
    const zipInput = this.calculator.querySelector('#address_zip');

    if (calculateBtn) {
      calculateBtn.addEventListener('click', (e) => this.handleCalculate(e));
    }

    if (zipInput) {
      zipInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.handleCalculate(e);
        }
      });
    }
  }

  setDefaultValues() {
    const countrySelect = this.calculator.querySelector('#address_country');
    const provinceSelect = this.calculator.querySelector('#address_province');

    if (!countrySelect) return;

    const defaultCountry = countrySelect.dataset.default || countrySelect.value;
    
    if (defaultCountry && defaultCountry !== '' && defaultCountry !== '---') {
      countrySelect.value = defaultCountry;
      this.updateSelectLabel(countrySelect);
      
      setTimeout(() => {
        if (provinceSelect && provinceSelect.options.length > 1) {
          const defaultProvince = provinceSelect.dataset.default;
          if (defaultProvince && defaultProvince !== '') {
            provinceSelect.value = defaultProvince;
            this.updateSelectLabel(provinceSelect);
          }
        }
      }, 500);
    } else {
      this.updateSelectLabel(countrySelect);
    }
  }

  updateSelectLabels() {
    const selects = this.calculator.querySelectorAll('select');
    selects.forEach(select => {
      select.addEventListener('change', () => this.updateSelectLabel(select));
      this.updateSelectLabel(select);
    });
  }

  updateSelectLabel(select) {
    const selectedText = select.previousElementSibling;
    if (!selectedText || !selectedText.classList.contains('selected-text')) return;

    const selectedOption = select.options[select.selectedIndex];
    if (selectedOption && selectedOption.value && selectedOption.value !== '') {
      selectedText.textContent = selectedOption.textContent;
    } else {
      const defaultText = selectedText.dataset.default || '';
      selectedText.textContent = defaultText;
    }
  }

  async handleCalculate(e) {
    const button = e.target.closest('.get-rates');
    if (!button || button.classList.contains('disabled')) return;

    const shippingAddress = {
      zip: this.calculator.querySelector('#address_zip')?.value?.trim() || '',
      country: this.calculator.querySelector('#address_country')?.value || '',
      province: this.calculator.querySelector('#address_province')?.value || ''
    };

    if (shippingAddress.country === '---' || !shippingAddress.country) {
      shippingAddress.country = '';
    }

    if (!shippingAddress.country) {
      this.showError('Please select a country');
      return;
    }

    if (!shippingAddress.province || shippingAddress.province === '') {
      delete shippingAddress.province;
    }

    if (!shippingAddress.zip || shippingAddress.zip === '') {
      delete shippingAddress.zip;
    }

    this.setButtonState(button, true);
    this.responseContainer.innerHTML = '';
    this.responseContainer.style.display = 'none';

    try {
      const rates = await this.getShippingRates(shippingAddress);
      this.displayRates(rates, shippingAddress);
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setButtonState(button, false);
    }
  }

  async getShippingRates(shippingAddress) {
    const params = new URLSearchParams();
    params.append('shipping_address[country]', shippingAddress.country || '');
    
    if (shippingAddress.province && shippingAddress.province !== '') {
      params.append('shipping_address[province]', shippingAddress.province);
    }
    
    if (shippingAddress.zip && shippingAddress.zip !== '') {
      params.append('shipping_address[zip]', shippingAddress.zip);
    }

    const response = await fetch(`/cart/shipping_rates.json?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    });

    if (!response.ok) {
      let errorMessage = `Failed to get shipping rates (${response.status})`;
      try {
        const errorData = await response.json();
        if (errorData.description) {
          errorMessage = errorData.description;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.errors) {
          const errorMessages = [];
          Object.keys(errorData.errors).forEach(key => {
            errorData.errors[key].forEach(msg => {
              errorMessages.push(`${key}: ${msg}`);
            });
          });
          errorMessage = errorMessages.join('; ') || errorMessage;
        }
      } catch (e) {
        // Use default error message
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data.shipping_rates || [];
  }

  displayRates(rates, shippingAddress) {
    const addressParts = [];
    if (shippingAddress.zip) addressParts.push(shippingAddress.zip);
    if (shippingAddress.province) addressParts.push(shippingAddress.province);
    if (shippingAddress.country) addressParts.push(shippingAddress.country);
    const address = addressParts.join(', ');

    let message = '';
    
    if (rates.length > 1) {
      const firstRate = this.formatRate(rates[0].price);
      message = (this.strings.multiRates || 'There are {{ number_of_rates }} shipping rates available for {{ address }}, starting at {{ rate }}.')
        .replace('** number_of_rates **', rates.length)
        .replace('** address **', address)
        .replace('** rate **', `<span class="money">${firstRate}</span>`);
    } else if (rates.length === 1) {
      message = (this.strings.oneRate || 'Shipping rates for {{ address }}:')
        .replace('** address **', address);
    } else {
      message = this.strings.noRates || 'We do not ship to this destination.';
    }

    let html = `<p class="shipping-calculator-response message">${message}</p>`;
    
    if (rates.length > 0) {
      html += '<ul class="shipping-rates">';
      rates.forEach(rate => {
        const price = this.formatRate(rate.price);
        const rateText = `${rate.name || ''} at <span class="money">${price}</span>`;
        html += `<li>${rateText}</li>`;
      });
      html += '</ul>';
    }

    this.responseContainer.innerHTML = html;
    this.responseContainer.style.display = 'block';
  }

  handleError(error) {
    let errorMessage = 'An error occurred while calculating shipping rates.';
    
    if (error.message) {
      if (error.message.includes('not ship')) {
        errorMessage = 'We do not ship to this destination.';
      } else {
        errorMessage = error.message;
      }
    }

    this.responseContainer.innerHTML = `<p class="shipping-calculator-error">${errorMessage}</p>`;
    this.responseContainer.style.display = 'block';
  }

  setButtonState(button, disabled) {
    if (disabled) {
      button.classList.add('disabled');
      button.value = this.strings.submitButtonProcessing || 'Calculating...';
      button.disabled = true;
    } else {
      button.classList.remove('disabled');
      button.value = this.strings.submitButton || 'CALCULATE';
      button.disabled = false;
    }
  }

  formatRate(cents) {
    let centsValue = cents;
    if (typeof cents === 'string') {
      centsValue = parseFloat(cents.replace('.', '')) || 0;
    } else {
      centsValue = cents || 0;
    }

    const dollars = centsValue / 100;
    const formatString = this.moneyFormat;
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(pattern);
    
    if (!match) {
      return `$${dollars.toFixed(2)}`;
    }

    const placeholder = match[1];
    let formattedValue = '';

    switch (placeholder) {
      case 'amount':
        formattedValue = this.addCommas(dollars.toFixed(2));
        break;
      case 'amount_no_decimals':
        formattedValue = this.addCommas(Math.round(dollars).toString());
        break;
      case 'amount_with_comma_separator':
        formattedValue = dollars.toFixed(2).replace('.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formattedValue = Math.round(dollars).toString();
        break;
      case 'amount_with_space_separator':
        formattedValue = dollars.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        break;
      case 'amount_no_decimals_with_space_separator':
        formattedValue = Math.round(dollars).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        formattedValue = dollars.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'");
        break;
      default:
        formattedValue = dollars.toFixed(2);
    }

    return formatString.replace(pattern, formattedValue);
  }

  addCommas(value) {
    const parts = value.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  showError(message) {
    this.responseContainer.innerHTML = `<p class="shipping-calculator-error">${message}</p>`;
    this.responseContainer.style.display = 'block';
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initShippingCalculators);
} else {
  initShippingCalculators();
}

function initShippingCalculators() {
  const calculators = document.querySelectorAll('[data-shipping-calculator]');
  calculators.forEach(container => {
    new ShippingCalculator(container);
  });
}

document.addEventListener('shopify:section:load', (e) => {
  const calculator = e.target.querySelector('[data-shipping-calculator]');
  if (calculator) {
    new ShippingCalculator(calculator);
  }
});
